/* REAI theme — global.js: nav state, mobile menu, reveals, scroll-reveal hero */
(function () {
  "use strict";

  var reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (!reducedMotion) document.documentElement.classList.add("js");

  /* ---------- nav scroll state + promo offset ----------
     The promo bar (sections/promo-banner.liquid) sits above the header in normal
     flow, so it scrolls away by itself. The header and frame are fixed and need
     telling: --promo-offset is whatever is left of the bar on screen, and both
     read it in CSS. A direct function of scrollY, so it tracks smoothly rather
     than snapping at a threshold. Zero when the banner is switched off. */
  var nav = document.getElementById("nav");
  var ticking = false;
  var promoH = 0;
  var promoObserver = null;
  var PROMO_KEY = "reai:promo-dismissed";

  /* Looked up fresh every time rather than cached at load.

     The first version captured the element and its height once. That broke in the
     theme editor: toggling "Show the promo banner" re-renders only that section
     via Shopify's Section Rendering API, so this file never runs again. It had
     recorded height 0 while the banner was off, setPromoOffset() bailed out, and
     the fixed header sat on top of the newly rendered bar. */
  function promoEl() {
    var b = document.getElementById("promoBar");
    return b && !b.hidden ? b : null;
  }

  function setPromoOffset() {
    /* always write it, including 0 — the old guard meant the offset could never
       be cleared once set, so dismissing the bar left the header pushed down */
    var left = Math.max(0, promoH - window.scrollY);
    document.documentElement.style.setProperty("--promo-offset", left + "px");
  }

  function measurePromo() {
    var bar = promoEl();
    promoH = bar ? bar.offsetHeight : 0;
    setPromoOffset();
  }

  function bindPromo() {
    var bar = promoEl();
    measurePromo();
    if (!bar || bar.dataset.promoBound === "1") return;
    bar.dataset.promoBound = "1";

    /* dismissal is keyed to the wording, so changing the promo shows it again to
       someone who dismissed the previous one */
    var msg = bar.querySelector(".promo-message");
    var text = msg ? msg.innerHTML : "1";
    if (bar.hasAttribute("data-promo-dismissible")) {
      try {
        if (window.localStorage.getItem(PROMO_KEY) === text) {
          bar.hidden = true;
          measurePromo();
        }
      } catch (e) { /* storage unavailable — just show it */ }
    }

    var close = bar.querySelector("[data-promo-close]");
    if (close) {
      close.addEventListener("click", function () {
        try { window.localStorage.setItem(PROMO_KEY, text); }
        catch (e) { /* dismissal just will not persist */ }
        bar.hidden = true;
        measurePromo();
      });
    }

    /* height changes on its own — fonts finishing, the message wrapping to two
       lines on a narrow screen — so watch the element rather than only resize */
    if (window.ResizeObserver) {
      if (promoObserver) promoObserver.disconnect();
      promoObserver = new ResizeObserver(measurePromo);
      promoObserver.observe(bar);
    }
  }

  if (nav) {
    var onScroll = function () {
      if (!ticking) {
        window.requestAnimationFrame(function () {
          nav.classList.toggle("is-scrolled", window.scrollY > 24 + promoH);
          setPromoOffset();
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
  }

  bindPromo();
  window.addEventListener("resize", measurePromo);

  /* ---------- header height, for anything that sticks beneath it ----------
     The filter bar needs to know where the header ends. That height is not a
     constant: the accessibility text-size control scales it, the logo can
     reflow, and in the theme editor the header re-renders on its own. So
     measure the element, the same way the promo bar is measured above. */
  function measureHeader() {
    if (!nav) return;
    document.documentElement.style.setProperty("--header-h", nav.offsetHeight + "px");
  }
  measureHeader();
  window.addEventListener("resize", measureHeader);
  if (window.ResizeObserver && nav) {
    new ResizeObserver(measureHeader).observe(nav);
  }
  document.addEventListener("shopify:section:load", measureHeader);

  /* ---------- the filter panel ----------
     Deliberately NOT tied to scrolling. The first version stuck the whole
     toolbar and folded it once it engaged, which changed the document height by
     336px every time — measured at the foot of the shop page, reopening it threw
     the scroll position from 2148 to 2484. Because the jump depended on where
     you were, it looked intermittent.

     Now the bar is one compact line at every scroll position and the controls
     open in an absolutely positioned panel, so the page height never changes.
     is-stuck survives only to swap in the frosted background; it moves nothing. */
  var toolsTicking = false;
  var toolsBound = false;

  function paintToolsStuck() {
    var bar = document.getElementById("shopToolsBar");
    if (!bar) return;
    var top = parseFloat(window.getComputedStyle(bar).top);
    if (isNaN(top)) return;
    bar.classList.toggle("is-stuck", bar.getBoundingClientRect().top <= top + 1);
  }

  function bindFilterPanel() {
    var bar = document.getElementById("shopToolsBar");
    if (!bar) return;
    var toggle = document.getElementById("toolsToggle");
    var panel = bar.querySelector(".tools-collapse");
    if (!toggle || !panel) return;

    var setOpen = function (open) {
      bar.classList.toggle("is-collapsed", !open);
      toggle.setAttribute("aria-expanded", open ? "true" : "false");
    };
    var isOpen = function () { return !bar.classList.contains("is-collapsed"); };

    if (!toggle.dataset.bound) {
      toggle.dataset.bound = "1";
      bar.classList.add("has-js");
      toggle.hidden = false;
      setOpen(false);
      toggle.addEventListener("click", function () { setOpen(!isOpen()); });
    }

    if (!toolsBound) {
      toolsBound = true;
      /* Non-modal, matching the nav panel: outside click and Escape close it. */
      document.addEventListener("click", function (e) {
        var b = document.getElementById("shopToolsBar");
        if (!b || b.classList.contains("is-collapsed")) return;
        if (b.contains(e.target)) return;
        b.classList.add("is-collapsed");
        var t = document.getElementById("toolsToggle");
        if (t) t.setAttribute("aria-expanded", "false");
      });
      document.addEventListener("keydown", function (e) {
        if (e.key !== "Escape") return;
        var b = document.getElementById("shopToolsBar");
        if (!b || b.classList.contains("is-collapsed")) return;
        b.classList.add("is-collapsed");
        var t = document.getElementById("toolsToggle");
        if (t) { t.setAttribute("aria-expanded", "false"); t.focus(); }
      });
      window.addEventListener("scroll", function () {
        if (toolsTicking) return;
        toolsTicking = true;
        window.requestAnimationFrame(function () { paintToolsStuck(); toolsTicking = false; });
      }, { passive: true });
      window.addEventListener("resize", paintToolsStuck);
    }
    paintToolsStuck();
  }

  bindFilterPanel();
  document.addEventListener("shopify:section:load", bindFilterPanel);

  /* ---------- browsable photos on the product card ----------
     The track is a real scroll container, so the swipe, the momentum and the
     snapping are the browser's. This only adds what a scroller cannot do on its
     own: arrows that page by exactly one frame, dots that follow, and disabling
     an arrow at the end so it is not offered when it would do nothing.

     Everything reads scrollLeft, so a finger and the arrows cannot disagree. */
  function bindCardGalleries(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    var galleries = root.querySelectorAll("[data-card-gallery]");

    Array.prototype.forEach.call(galleries, function (gal) {
      if (gal.dataset.bound) return;
      gal.dataset.bound = "1";

      var track = gal.querySelector("[data-card-track]");
      var prev  = gal.querySelector("[data-card-prev]");
      var next  = gal.querySelector("[data-card-next]");
      var dots  = gal.querySelector("[data-card-dots]");
      if (!track) return;
      var slides = track.children.length;
      if (slides < 2) return;
      var dotEls = dots ? dots.children : [];

      function index() {
        /* round rather than floor: a snap can settle a fraction of a pixel short
           and floor would then report the previous photo */
        return Math.round(track.scrollLeft / track.clientWidth);
      }

      function paint() {
        var i = index();
        for (var d = 0; d < dotEls.length; d++) {
          dotEls[d].classList.toggle("is-on", d === i);
        }
        /* scrollWidth - clientWidth is the furthest scrollLeft can reach; the 2px
           of slack absorbs sub-pixel track widths, which otherwise leave the next
           arrow enabled on the last photo forever */
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
      }

      /* Paging is an explicit slide offset, never a relative scrollBy: measured on
         the live grid, scrollBy was swallowed on 3 clicks out of 4.

         The fallback matters more. scrollTo with behavior:"smooth" is driven by
         the compositor and does not always run — in a hidden or non-compositing
         view it moved the track nothing at all, every time, while an instant
         scroll always worked. So the smooth scroll is requested, and if the
         track has not budged a moment later it is placed directly. The arrow can
         then never be a dead control, whatever the browser decides about
         animating it. */
      function goTo(target) {
        target = Math.max(0, Math.min(slides - 1, target));
        var child = track.children[target];
        if (!child) return;
        var left = child.offsetLeft;
        var from = track.scrollLeft;
        if (Math.abs(from - left) < 2) return;
        var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (reduce) { track.scrollLeft = left; return; }
        track.scrollTo({ left: left, behavior: "smooth" });
        setTimeout(function () {
          if (Math.abs(track.scrollLeft - from) < 2) track.scrollLeft = left;
        }, 150);
      }

      function page(dir) { goTo(index() + dir); }

      /* Hover preview, restored. Before the gallery existed, hovering a card
         crossfaded to its second photo; that was dropped because an overlaid
         image and a scrolling track can disagree about which photo is showing.
         Hovering now drives the same scroll the arrows do, so there is only one
         source of truth and no way for the two to desync. It reads as a slide
         rather than a fade.

         Pointer devices only — on a phone there is no hover, and a touch that
         registered as one would hijack the swipe. Entering only advances from
         the first photo, so moving the mouse within a card the shopper has
         already paged through does not yank it back and forth; leaving returns
         it to the primary shot. */
      if (window.matchMedia && window.matchMedia("(hover: hover)").matches) {
        var hoverTarget = gal.closest ? (gal.closest(".piece") || gal) : gal;
        hoverTarget.addEventListener("mouseenter", function () {
          if (index() === 0) goTo(1);
        });
        hoverTarget.addEventListener("mouseleave", function () {
          goTo(0);
        });
      }

      if (prev) prev.addEventListener("click", function (e) {
        /* the arrows sit over the product link; without this a click would both
           page the photos and open the product */
        e.preventDefault(); e.stopPropagation(); page(-1);
      });
      if (next) next.addEventListener("click", function (e) {
        e.preventDefault(); e.stopPropagation(); page(1);
      });

      var ticking = false;
      track.addEventListener("scroll", function () {
        if (ticking) return;
        ticking = true;
        /* a timer, not requestAnimationFrame: rAF is paused outright in a
           background or non-compositing view, and the dots then sit frozen on a
           photo the shopper has already scrolled past. paint() only toggles a
           few classes, so a short coalescing window costs nothing and always
           runs. */
        setTimeout(function () { paint(); ticking = false; }, 60);
      }, { passive: true });

      /* the card can be hidden by a filter, or start off-screen, and clientWidth
         is 0 until it is laid out — re-measure when the size finally arrives */
      if (window.ResizeObserver) new ResizeObserver(paint).observe(track);
      paint();
    });
  }

  bindCardGalleries();
  document.addEventListener("shopify:section:load", function (e) {
    bindCardGalleries(e && e.target ? e.target : document);
  });

  /* theme editor: fired when a section is re-rendered or removed */
  document.addEventListener("shopify:section:load", bindPromo);
  document.addEventListener("shopify:section:unload", measurePromo);
  document.addEventListener("shopify:section:select", measurePromo);

  /* Deep links into a <details> (e.g. /pages/faq#returns from the footer).
     The FAQ accordions are native <details>, so without this a link lands on a
     closed panel and reads as broken. */
  function openHashDetails() {
    if (!window.location.hash) return;
    var target;
    try { target = document.querySelector(window.location.hash); } catch (e) { return; }
    if (!target) return;
    var panel = target.closest ? target.closest("details") : null;
    if (!panel) return;
    panel.open = true;
    /* re-run the scroll now the panel has height — the browser's own jump
       happened while it was still collapsed. scroll-margin-top on .faq details
       is what clears the fixed nav. */
    window.requestAnimationFrame(function () {
      panel.scrollIntoView({ block: "start", behavior: reducedMotion ? "auto" : "smooth" });
    });
  }
  window.addEventListener("hashchange", openHashDetails);
  openHashDetails();

  /* ---------- marquee pause (WCAG 2.2.2 Pause, Stop, Hide — Level A) ----------
     Moving content that starts on its own and runs past 5s needs an on-page way to
     stop it. Hover/focus pausing lives in CSS; this adds the explicit toggle.

     The choice is remembered in localStorage: someone who stops the motion because
     it bothers them should not have to stop it again on all 14 pages. Wrapped in
     try/catch because Safari private mode throws on localStorage access. */
  var MARQUEE_KEY = "reai:marquee-paused";

  function marqueeStored() {
    try { return window.localStorage.getItem(MARQUEE_KEY) === "1"; }
    catch (e) { return false; }
  }
  function marqueeStore(paused) {
    try {
      if (paused) window.localStorage.setItem(MARQUEE_KEY, "1");
      else window.localStorage.removeItem(MARQUEE_KEY);
    } catch (e) { /* storage unavailable — the toggle still works for this page */ }
  }

  var marqueeButtons = document.querySelectorAll("[data-marquee-pause]");
  if (marqueeButtons.length) {
    var startPaused = marqueeStored();

    var applyMarquee = function (paused) {
      marqueeButtons.forEach(function (btn) {
        var strip = btn.closest(".marquee");
        if (strip) strip.classList.toggle("is-paused", paused);
        btn.setAttribute(
          "aria-label",
          paused ? "Resume the scrolling claims" : "Pause the scrolling claims"
        );
      });
    };

    applyMarquee(startPaused);

    marqueeButtons.forEach(function (btn) {
      btn.addEventListener("click", function () {
        var strip = btn.closest(".marquee");
        var nowPaused = !(strip && strip.classList.contains("is-paused"));
        applyMarquee(nowPaused);
        marqueeStore(nowPaused);
      });
    });
  }

  /* ---------- featured carousel ----------
     Progressive enhancement: the track scrolls natively (swipe, trackpad,
     keyboard via the card links), and this only adds arrow controls, disabled
     states, and the right-edge fade. The nav is hidden until we get here, so a
     JS failure leaves a working scroller rather than dead buttons. */
  document.querySelectorAll(".piece-carousel").forEach(function (carousel) {
    var track = carousel.querySelector(".piece-track");
    var prev = carousel.querySelector('[data-dir="prev"]');
    var next = carousel.querySelector('[data-dir="next"]');
    if (!track || !prev || !next) return;

    function maxScroll() {
      return track.scrollWidth - track.clientWidth;
    }

    function sync() {
      var x = Math.round(track.scrollLeft);
      var max = Math.round(maxScroll());
      /* The track carries 2px of inline padding so focus rings on the first and
         last card are not clipped by the scroll container. Snap points sit inside
         that padding, so a track at rest reports scrollLeft 2, not 0 — measure the
         padding rather than hard-coding slack, or "prev" never disables. */
      var pad = parseFloat(getComputedStyle(track).paddingLeft) || 0;
      var atStart = x <= pad + 1;
      var atEnd = x >= max - 1;
      prev.disabled = atStart;
      next.disabled = atEnd;
      carousel.setAttribute("data-at-end", atEnd ? "true" : "false");
    }

    function page(dir) {
      /* one viewport of cards; scroll-snap tidies up the landing position */
      track.scrollBy({
        left: dir * track.clientWidth,
        behavior: reducedMotion ? "auto" : "smooth"
      });
    }

    carousel.classList.add("is-enhanced");
    prev.addEventListener("click", function () { page(-1); });
    next.addEventListener("click", function () { page(1); });
    track.addEventListener("scroll", sync, { passive: true });
    window.addEventListener("resize", sync);
    /* card images are lazy — widths change as they land, so re-check then too */
    track.querySelectorAll("img").forEach(function (img) {
      if (!img.complete) img.addEventListener("load", sync, { once: true });
    });
    sync();
  });

  var toggle = document.getElementById("navToggle");
  var menu = document.getElementById("mobileMenu");
  if (toggle && menu) {
    /* compact anchored dropdown (non-modal): toggle, Escape, outside click */
    function closeMenu(restoreFocus) {
      menu.classList.remove("is-open");
      toggle.setAttribute("aria-expanded", "false");
      if (subs) subs.classList.remove("is-open");
      if (subToggle) subToggle.setAttribute("aria-expanded", "false");
      if (restoreFocus) toggle.focus();
    }
    toggle.addEventListener("click", function () {
      var open = menu.classList.toggle("is-open");
      toggle.setAttribute("aria-expanded", String(open));
      if (open) {
        var first = menu.querySelector("a[href]");
        if (first) window.setTimeout(function () { first.focus(); }, 60);
      } else {
        closeMenu(false);
      }
    });
    document.addEventListener("click", function (e) {
      if (
        menu.classList.contains("is-open") &&
        !menu.contains(e.target) &&
        !toggle.contains(e.target)
      ) {
        closeMenu(false);
      }
    });

    /* collapsible sub-menu group inside the mobile menu */
    var subToggle = document.getElementById("mobileSubToggle");
    var subs = document.getElementById("mobileSubs");
    if (subToggle && subs) {
      subToggle.addEventListener("click", function () {
        var open = subs.classList.toggle("is-open");
        subToggle.setAttribute("aria-expanded", String(open));
        subToggle.setAttribute("aria-label", open ? "Hide collection categories" : "Show collection categories");
      });
    }
    menu.addEventListener("click", function (e) {
      if (e.target.tagName === "A") closeMenu(false);
    });
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && menu.classList.contains("is-open")) {
        closeMenu(true);
      }
    });
  }

  var reveals = document.querySelectorAll(".reveal");
  if (reducedMotion || !("IntersectionObserver" in window)) {
    reveals.forEach(function (el) { el.classList.add("in-view"); });
  } else {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add("in-view");
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: "0px 0px -8% 0px" });
    reveals.forEach(function (el) { io.observe(el); });
  }

  /* Shop: price scale + sort. Filters the products on the current page.
     Runs over ONE grid (a collection page) or SEVERAL stacked grids (the
     collections index, where each category has its own). Sorting stays inside
     each grid so the category structure survives, and a category whose pieces
     all filter out hides its heading with them — otherwise the page shows a run
     of headings with nothing under them. */
  var grids = Array.prototype.slice.call(
    document.querySelectorAll("#shopGrid, [data-shop-grid]")
  );
  var minEl = document.getElementById("priceMin");
  var maxEl = document.getElementById("priceMax");

  if (grids.length && minEl && maxEl) {
    var fill = document.getElementById("rangeFill");
    var lowOut = document.getElementById("rangeLow");
    var highOut = document.getElementById("rangeHigh");
    var sortEl = document.getElementById("sortBy");
    var countEl = document.getElementById("resultCount");
    var emptyEl = document.getElementById("noResults");
    var searchEl = document.getElementById("shopSearch");
    var searchClear = document.getElementById("shopSearchClear");
    var shapeChips = document.getElementById("shapeChips");
    var shapeBoxes = shapeChips
      ? Array.prototype.slice.call(shapeChips.querySelectorAll("input[type=checkbox]"))
      : [];
    var shapeReset = document.getElementById("shapeReset");
    var toolsBadge = document.getElementById("toolsBadge");
    var categoryControl = document.querySelector(".category-control");
    var categoryChips = document.getElementById("categoryChips");
    var catBoxes = categoryChips
      ? Array.prototype.slice.call(categoryChips.querySelectorAll("input[type=checkbox]"))
      : [];
    var categoryReset = document.getElementById("categoryReset");

    /* Search matches WHOLE WORDS, prefix-wise — "ring" finds Ring and Rings but
       never Earrings, which a plain substring test matched because "earrings"
       literally ends in "rings". Same class of bug piece-card.liquid already
       guards against with "pear" inside "pearl".

       A trailing "s" is also tried without it, because every product title says
       "Ring" singular: word-prefix alone made the plural "rings" — the obvious
       thing to type — return nothing at all. So "rings" matches Ring, and
       "earrings" still matches Earrings.

       The \b is only prepended when the term starts with a word character;
       "\b-" would never match anything. Prefix matching means "pear" also finds
       "Pearl", which is normal search-box behaviour and left alone. */
    var searchPattern = function (term) {
      var escape = function (t) { return t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); };
      var forms = [escape(term)];
      if (/[a-z]s$/.test(term)) forms.push(escape(term.slice(0, -1)));
      return new RegExp(
        (/^\w/.test(term) ? "\\b" : "") + "(?:" + forms.join("|") + ")", "i"
      );
    };

    /* What a search term is actually tested against.

       The name alone meant a piece only answered to its own name: the huggies sit
       in the Earrings collection but never surfaced for "earrings", which is the
       obvious thing to type. Categories are the COLLECTIONS the piece belongs to,
       so this follows whatever is set in the admin rather than a synonym list
       that would need feeding forever. Hyphens become spaces so the  in the
       search pattern can still bite on "engagement-rings".

       data-keywords adds the product type, tags and collection titles. */
    var cardSearchText = function (card) {
      if (card._searchText === undefined) {
        var nameEl = card.querySelector("h3");
        card._searchText = [
          nameEl ? nameEl.textContent : "",
          (card.dataset.category || "").replace(/-/g, " "),
          card.dataset.keywords || ""
        ].join(" ");
      }
      return card._searchText;
    };

    /* Categories come from the collections a piece belongs to (piece-card.liquid
       writes the handles into data-category), so they follow whatever is set in
       Shopify rather than a second list to keep in step. */
    var cardCategories = function (card) {
      return (card.dataset.category || "").split(" ").filter(Boolean);
    };

    var cards = [];
    grids.forEach(function (g) {
      Array.prototype.push.apply(cards, Array.prototype.slice.call(g.querySelectorAll(".piece")));
    });
    /* one wrapper per category on the collections index; empty elsewhere */
    var groups = Array.prototype.slice.call(document.querySelectorAll("[data-filter-group]"));
    var floor = parseInt(minEl.min, 10);
    var ceiling = parseInt(minEl.max, 10);
    var span = ceiling - floor;

    cards.forEach(function (card, i) { card.dataset.order = i; });

    var money = function (n) { return "$" + n.toLocaleString("en-US"); };

    var apply = function () {
      var lo = parseInt(minEl.value, 10);
      var hi = parseInt(maxEl.value, 10);

      if (lo > hi) {
        if (document.activeElement === minEl) { hi = lo; maxEl.value = lo; }
        else { lo = hi; minEl.value = hi; }
      }

      fill.style.left = ((lo - floor) / span) * 100 + "%";
      fill.style.width = ((hi - lo) / span) * 100 + "%";
      lowOut.textContent = money(lo);
      highOut.textContent = money(hi);
      minEl.setAttribute("aria-valuetext", money(lo));
      maxEl.setAttribute("aria-valuetext", money(hi));

      /* search matches the piece name; shape reads data-shape, which piece-card
         fills from the product tags */
      var term = searchEl ? searchEl.value.trim().toLowerCase() : "";
      var termRe = term ? searchPattern(term) : null;
      var shapes = shapeBoxes.filter(function (b) { return b.checked; })
                             .map(function (b) { return b.value; });
      var cats = catBoxes.filter(function (b) { return b.checked; })
                         .map(function (b) { return b.value; });

      var shown = 0;
      cards.forEach(function (card) {
        var price = parseInt(card.dataset.price, 10);
        var visible = price >= lo && price <= hi;

        if (visible && shapes.length) {
          visible = shapes.indexOf(card.dataset.shape || "other") !== -1;
        }
        if (visible && cats.length) {
          visible = cardCategories(card).some(function (h) {
            return cats.indexOf(h) !== -1;
          });
        }
        if (visible && termRe) {
          visible = termRe.test(cardSearchText(card));
        }

        card.hidden = !visible;
        if (visible) { shown++; card.classList.add("in-view"); }
      });

      groups.forEach(function (group) {
        group.hidden = !group.querySelector(".piece:not([hidden])");
      });

      if (searchClear) searchClear.hidden = term === "";
      if (shapeReset) shapeReset.hidden = shapes.length === 0;
      if (categoryReset) categoryReset.hidden = cats.length === 0;

      /* Badge on the folded bar counts filter GROUPS, not ticked boxes. Four
         shape chips is still one kind of narrowing; showing "4" there would
         suggest the price and search had been touched too. */
      if (toolsBadge) {
        var active = 0;
        if (lo > floor || hi < floor + span) active++;
        if (term !== "") active++;
        if (shapes.length) active++;
        if (cats.length) active++;
        toolsBadge.hidden = active === 0;
        toolsBadge.textContent = String(active);
      }

      /* Wording comes from the theme's translations via data attributes, so the
         merchant can reword it in Edit default theme content like every other
         string. Falls back to the English if the attributes are ever absent. */
      if (countEl) {
        var wAll = countEl.dataset.all || "Showing all";
        var wSome = countEl.dataset.some || "Showing";
        var wOf = countEl.dataset.of || "of";
        var wUnit = countEl.dataset.unit || "pieces";
        countEl.textContent = shown === cards.length
          ? wAll + " " + cards.length + " " + wUnit
          : wSome + " " + shown + " " + wOf + " " + cards.length + " " + wUnit;
      }
      if (emptyEl) emptyEl.hidden = shown > 0;

      queueState();
    };

    var sort = function () {
      var mode = sortEl.value;
      grids.forEach(function (g) {
        Array.prototype.slice.call(g.querySelectorAll(".piece")).sort(function (a, b) {
          if (mode === "price-asc") return a.dataset.price - b.dataset.price;
          if (mode === "price-desc") return b.dataset.price - a.dataset.price;
          return a.dataset.order - b.dataset.order;
        }).forEach(function (card) { g.appendChild(card); });
      });
    };

    /* ---------- the filters live in the query string ----------
       The filtering is client-side, so without this the state exists only in
       the DOM: tick Wedding bands, open a piece, press Back, and the browser
       hands back a fresh page with every filter cleared and the shopper at the
       top of the full catalogue again. Keeping the state in the URL means Back
       restores it, and a narrowed view becomes something that can be
       bookmarked or sent to a client.

       replaceState, never pushState: pushing would make every chip click its
       own history entry, so Back would walk backwards through filter changes
       one at a time instead of leaving the page -- the opposite of what was
       asked for.

       The URL is the single source of truth. A key that is absent means the
       default, which is why each restore writes BOTH branches rather than only
       applying what it finds: a reload must not leave a tickbox the browser
       restored on its own disagreeing with the address bar. */
    var canStoreState = !!(window.history && window.history.replaceState && window.URLSearchParams);

    var checkedValues = function (boxes) {
      return boxes.filter(function (b) { return b.checked; })
                  .map(function (b) { return b.value; });
    };

    var writeState = function () {
      if (!canStoreState) return;
      /* Seeded from the live query string, then only our own keys are touched,
         so page=2, preview_theme_id and any campaign tag survive a filter
         change instead of being dropped on the floor. */
      var p = new URLSearchParams(window.location.search);
      var set = function (key, value, isDefault) {
        if (isDefault) p.delete(key); else p.set(key, value);
      };
      var lo = parseInt(minEl.value, 10);
      var hi = parseInt(maxEl.value, 10);
      var term = searchEl ? searchEl.value.trim() : "";
      var shapes = checkedValues(shapeBoxes);
      var cats = checkedValues(catBoxes);

      set("min", lo, lo <= floor);
      set("max", hi, hi >= ceiling);
      set("q", term, term === "");
      set("shape", shapes.join(","), shapes.length === 0);
      set("category", cats.join(","), cats.length === 0);
      set("sort", sortEl ? sortEl.value : "", !sortEl || sortEl.value === "featured");

      var qs = p.toString();
      var url = window.location.pathname + (qs ? "?" + qs : "") + window.location.hash;
      if (url === window.location.pathname + window.location.search + window.location.hash) return;
      window.history.replaceState(window.history.state, "", url);
    };

    var stateTimer = null;
    var queueState = function () {
      if (!canStoreState) return;
      /* Dragging a slider fires input on every pixel, and Safari throttles
         replaceState -- past roughly a hundred calls in thirty seconds it
         starts dropping them and logging about it. So the writes coalesce. */
      if (stateTimer) clearTimeout(stateTimer);
      stateTimer = setTimeout(writeState, 250);
    };

    var restoreState = function () {
      if (!canStoreState) return false;
      var p = new URLSearchParams(window.location.search);
      var touched = false;

      var bound = function (key, el, fallback) {
        var raw = p.get(key);
        var v = raw === null ? NaN : parseInt(raw, 10);
        if (isNaN(v)) { el.value = fallback; return; }
        /* Clamped, because the catalogue moves: a ceiling bookmarked before a
           repricing would otherwise sit outside the track, and the browser
           would silently snap it somewhere of its own choosing. */
        el.value = Math.max(floor, Math.min(ceiling, v));
        touched = true;
      };
      bound("min", minEl, floor);
      bound("max", maxEl, ceiling);

      var boxes = function (key, list) {
        var raw = p.get(key);
        var want = raw === null ? [] : raw.split(",").map(function (s) { return s.trim(); });
        list.forEach(function (b) {
          /* a shape the catalogue has nothing in is disabled, and stays that
             way whatever the URL asks for */
          if (b.disabled) return;
          b.checked = want.indexOf(b.value) !== -1;
          if (b.checked) touched = true;
        });
      };
      boxes("shape", shapeBoxes);
      boxes("category", catBoxes);

      if (searchEl) {
        var q = p.get("q") || "";
        searchEl.value = q;
        if (q) touched = true;
      }

      if (sortEl) {
        var want = p.get("sort");
        var valid = want !== null && Array.prototype.some.call(sortEl.options, function (o) {
          return o.value === want;
        });
        sortEl.value = valid ? want : "featured";
        if (valid && want !== "featured") touched = true;
      }

      return touched;
    };

    minEl.addEventListener("input", apply);
    maxEl.addEventListener("input", apply);
    if (sortEl) {
      sortEl.addEventListener("change", function () { sort(); queueState(); });
    }
    /* Dim shapes this collection has nothing for — derived from the cards, so it
       stays honest as the catalogue changes. Dimmed, not removed: the full range
       of shapes Reai works in stays visible. */
    if (shapeBoxes.length) {
      var present = {};
      cards.forEach(function (card) { present[card.dataset.shape || "other"] = true; });
      shapeBoxes.forEach(function (b) {
        if (!present[b.value]) {
          b.disabled = true;
          b.closest(".shape-chip").title = "No pieces in this shape yet";
        }
      });
      shapeBoxes.forEach(function (b) { b.addEventListener("change", apply); });
    }
    if (shapeReset) {
      shapeReset.addEventListener("click", function () {
        shapeBoxes.forEach(function (b) { b.checked = false; });
        apply();
      });
    }
    /* Category chips. Unlike shapes these are REMOVED when the page has nothing
       in them, and the whole control goes away when fewer than two categories
       are represented — on /collections/earrings every piece is an earring, so
       a category filter there is a control that can only ever do nothing. */
    if (catBoxes.length) {
      var inPage = {};
      cards.forEach(function (card) {
        cardCategories(card).forEach(function (h) { inPage[h] = true; });
      });
      catBoxes = catBoxes.filter(function (b) {
        if (inPage[b.value]) return true;
        var chip = b.closest(".category-chip");
        if (chip) chip.remove();
        return false;
      });
      if (catBoxes.length < 2) {
        if (categoryControl) categoryControl.remove();
        catBoxes = [];
      } else {
        catBoxes.forEach(function (b) { b.addEventListener("change", apply); });
      }
    }
    if (categoryReset) {
      categoryReset.addEventListener("click", function () {
        catBoxes.forEach(function (b) { b.checked = false; });
        apply();
      });
    }
    if (searchEl) {
      searchEl.addEventListener("input", apply);
      /* type="search" fires this on the browser's own clear affordance too */
      searchEl.addEventListener("search", apply);
    }
    if (searchClear) {
      searchClear.addEventListener("click", function () {
        searchEl.value = "";
        apply();
        searchEl.focus();
      });
    }

    /* Restored last, after the chip setup above has disabled the shapes this
       catalogue has nothing in and removed the categories this page has none
       of -- so the URL can only ever select a control that really exists. */
    var restored = restoreState();
    /* Only reordered when the URL actually asks for it: sort() moves every
       card in the grid, which is wasted work on the common untouched visit. */
    if (restored && sortEl && sortEl.value !== "featured") sort();
    apply();
  }

  /* Marquee: the belt is two identical halves sliding by -50%. If one half is
     narrower than the screen it runs out of claims mid-slide and looks like it
     stalls, so clone the claim set until a half covers the viewport, then scale
     the duration to hold the scroll speed steady. */
  var PX_PER_SECOND = 32;
  Array.prototype.forEach.call(document.querySelectorAll(".marquee-track"), function (track) {
    var claims = Array.prototype.slice.call(track.children);
    if (claims.length < 2 || reducedMotion) return;

    var set = claims.slice(0, claims.length / 2);
    var setWidth = track.scrollWidth / 2;
    if (!setWidth) return;

    var target = Math.max(window.innerWidth, window.screen ? window.screen.width : 0) + 240;
    var setsPerHalf = Math.max(1, Math.ceil(target / setWidth));

    for (var i = 0; i < setsPerHalf * 2 - 2; i++) {
      set.forEach(function (claim) { track.appendChild(claim.cloneNode(true)); });
    }
    track.style.animationDuration = (setWidth * setsPerHalf / PX_PER_SECOND) + "s";
  });

  /* newsletter: inline email check before Shopify handles the submit */
  var news = document.querySelector('.footer-newsletter form');
  if (news) {
    news.addEventListener("submit", function (e) {
      var field = news.querySelector('input[type="email"]');
      if (!field) return;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(field.value.trim())) {
        e.preventDefault();
        field.setAttribute("aria-invalid", "true");
        field.focus();
      } else {
        field.setAttribute("aria-invalid", "false");
      }
    });
  }

  /* ---------- viewing options ----------
     Reader preferences, opened from a link in the footer. Both the trigger and
     the panel are BUILT HERE rather than sitting in the HTML: every control is
     JS-driven, so shipping the markup statically would leave dead buttons on the
     page if the script failed. It also means none of the 14 pages, and none of
     the Liquid templates, had to change.

     This is a comfort feature. It is not an accessibility fix and is never
     labelled as one — the conformance work is in the markup itself. */
  var VO_KEY = "reai:viewing";

  function voLoad() {
    try {
      var raw = window.localStorage.getItem(VO_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) { return {}; }   /* Safari private mode throws */
  }
  function voSave(state) {
    try { window.localStorage.setItem(VO_KEY, JSON.stringify(state)); }
    catch (e) { /* preferences still apply for this page */ }
  }

  var voState = voLoad();

  /* Applied on every page load, before the panel is built, so a stored choice
     is already in effect if the reader never opens the panel again. */
  function voApply() {
    var root = document.documentElement;
    root.classList.remove("vo-text-1", "vo-text-2");
    if (voState.text === 1) root.classList.add("vo-text-1");
    if (voState.text === 2) root.classList.add("vo-text-2");
    root.classList.toggle("vo-contrast", !!voState.contrast);
    root.classList.toggle("vo-no-motion", !!voState.noMotion);
    root.classList.toggle("vo-underline", !!voState.underline);
  }
  voApply();

  var voLegal = document.querySelector(".footer-legal");
  if (voLegal) {
    var wrap = document.createElement("div");
    wrap.className = "vo-wrap";

    var trigger = document.createElement("button");
    trigger.type = "button";
    trigger.className = "vo-footer-link";
    trigger.setAttribute("aria-expanded", "false");
    trigger.setAttribute("aria-controls", "voPanel");
    trigger.textContent = "Viewing options";

    var panel = document.createElement("div");
    panel.className = "vo-panel";
    panel.id = "voPanel";
    panel.setAttribute("role", "group");
    panel.setAttribute("aria-label", "Viewing options");

    panel.innerHTML =
      '<p class="vo-panel-head">Viewing options</p>' +
      '<div class="vo-row"><span class="vo-row-label" id="voTextLabel">Text size</span>' +
        '<div class="vo-steps" role="group" aria-labelledby="voTextLabel">' +
          '<button type="button" class="vo-step" data-vo-text="0" aria-label="Normal text size">A</button>' +
          '<button type="button" class="vo-step" data-vo-text="1" aria-label="Large text size">A</button>' +
          '<button type="button" class="vo-step" data-vo-text="2" aria-label="Largest text size">A</button>' +
        '</div></div>' +
      '<div class="vo-row"><span class="vo-row-label">Increase contrast</span>' +
        '<button type="button" class="vo-toggle" data-vo="contrast" aria-label="Increase contrast"></button></div>' +
      '<div class="vo-row"><span class="vo-row-label">Pause motion</span>' +
        '<button type="button" class="vo-toggle" data-vo="noMotion" aria-label="Pause motion"></button></div>' +
      '<div class="vo-row"><span class="vo-row-label">Underline links</span>' +
        '<button type="button" class="vo-toggle" data-vo="underline" aria-label="Underline links"></button></div>' +
      '<div class="vo-divider"></div>' +
      '<button type="button" class="vo-reset">Reset to default</button>';

    wrap.appendChild(trigger);
    wrap.appendChild(panel);
    voLegal.appendChild(wrap);

    var steps = panel.querySelectorAll("[data-vo-text]");
    var toggles = panel.querySelectorAll("[data-vo]");

    /* keeps the controls telling the truth about the current state */
    function voSync() {
      steps.forEach(function (b) {
        var on = (parseInt(b.getAttribute("data-vo-text"), 10) || 0) === (voState.text || 0);
        b.setAttribute("aria-pressed", String(on));
      });
      toggles.forEach(function (b) {
        b.setAttribute("aria-pressed", String(!!voState[b.getAttribute("data-vo")]));
      });
    }
    voSync();

    steps.forEach(function (b) {
      b.addEventListener("click", function () {
        voState.text = parseInt(b.getAttribute("data-vo-text"), 10) || 0;
        voApply(); voSync(); voSave(voState);
      });
    });

    toggles.forEach(function (b) {
      b.addEventListener("click", function () {
        var key = b.getAttribute("data-vo");
        voState[key] = !voState[key];
        voApply(); voSync(); voSave(voState);
      });
    });

    panel.querySelector(".vo-reset").addEventListener("click", function () {
      voState = {};
      voApply(); voSync(); voSave(voState);
    });

    function voClose(restoreFocus) {
      panel.classList.remove("is-open");
      trigger.setAttribute("aria-expanded", "false");
      if (restoreFocus) trigger.focus();
    }

    trigger.addEventListener("click", function () {
      var open = panel.classList.toggle("is-open");
      trigger.setAttribute("aria-expanded", String(open));
      if (open) {
        var first = panel.querySelector("button");
        if (first) window.setTimeout(function () { first.focus(); }, 60);
      }
    });

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && panel.classList.contains("is-open")) voClose(true);
    });

    document.addEventListener("click", function (e) {
      if (!panel.classList.contains("is-open")) return;
      if (!wrap.contains(e.target)) voClose(false);
    });
  }

  /* ---------- product gallery ----------
     Every media item is already in the page; the thumbs just switch which one is
     shown. Matched by media id rather than by index, so the pairing cannot drift
     if the two loops ever render different numbers of items. */
  var mediaWrap = document.getElementById("productMedia");
  var thumbs = document.querySelectorAll(".product-thumb");
  if (mediaWrap && thumbs.length) {
    var mediaItems = mediaWrap.querySelectorAll(".product-media");
    var autoplay = mediaWrap.getAttribute("data-autoplay") === "true";

    /* Remember each embed's original URL before we ever touch it, so autoplay
       params can be added and removed without compounding on the query string. */
    mediaItems.forEach(function (item) {
      var f = item.querySelector("iframe");
      if (f) f.setAttribute("data-base-src", f.src);
    });

    function showMedia(id, userInitiated) {
      mediaItems.forEach(function (item) {
        var on = item.getAttribute("data-media-id") === id;
        item.classList.toggle("is-active", on);

        var vid = item.querySelector("video");
        var iframe = item.querySelector("iframe");

        if (on) {
          /* Only on an actual thumbnail click, never on page load — a clip that
             starts by itself as the page opens is intrusive.

             MUTED is not a preference, it is the requirement: every browser
             refuses autoplay with sound, and the play() promise would just
             reject. Controls stay on so the shopper can unmute. */
          if (autoplay && userInitiated) {
            if (vid) {
              vid.muted = true;
              var p = vid.play();
              /* Safari and Firefox reject rather than throw; swallow it so a
                 blocked autoplay never surfaces as a console error */
              if (p && typeof p.catch === "function") { p.catch(function () {}); }
            } else if (iframe) {
              var base = iframe.getAttribute("data-base-src") || iframe.src;
              var join = base.indexOf("?") > -1 ? "&" : "?";
              iframe.src = base + join + "autoplay=1&muted=1&mute=1";
            }
          }
        } else {
          /* stop a clip that is playing when the shopper moves to another angle —
             audio continuing from a hidden video is disorienting */
          if (vid && !vid.paused) vid.pause();
          if (iframe) {
            var original = iframe.getAttribute("data-base-src");
            if (original && iframe.src !== original) iframe.src = original;
          }
        }
      });
      thumbs.forEach(function (t) {
        var on = t.getAttribute("data-media-id") === id;
        t.classList.toggle("is-active", on);
        t.setAttribute("aria-pressed", on ? "true" : "false");
      });
    }

    thumbs.forEach(function (thumb) {
      thumb.addEventListener("click", function () {
        showMedia(thumb.getAttribute("data-media-id"), true);
      });
    });
  }

  /* ---------- product options ----------
     One select per option, matched back to a real variant. Also enforces the
     rule that platinum has no metal colour to choose: when the metal option is
     set to platinum the colour select is hidden and its value is ignored, so a
     shopper is never asked a question with no meaningful answer.

     Matching is by option VALUES rather than index maths, so it survives the
     merchant reordering options in the admin. */
  var variantDataEl = document.getElementById("variantData");
  var optionSelects = Array.prototype.slice.call(document.querySelectorAll("[data-option-select]"));
  var variantIdInput = document.getElementById("variantId");

  if (variantDataEl && optionSelects.length && variantIdInput) {
    var variants = [];
    try { variants = JSON.parse(variantDataEl.textContent); }
    catch (e) { variants = []; }

    var priceEl = document.getElementById("productPrice");
    var addBtn = document.getElementById("addToCart");

    /* the colour select is whichever one offers yellow/white/rose */
    var colourSelect = optionSelects.filter(function (s) {
      return Array.prototype.some.call(s.options, function (o) {
        return /yellow|white|rose/i.test(o.value);
      });
    })[0];

    /* the metal select is whichever one offers platinum */
    var metalSelect = optionSelects.filter(function (s) {
      return Array.prototype.some.call(s.options, function (o) {
        return /platinum/i.test(o.value);
      });
    })[0];

    function money(cents) {
      return "$" + (cents / 100).toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      });
    }

    function chosen() {
      return optionSelects.map(function (s) { return s.value; });
    }

    function findVariant(values) {
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var match = true;
        for (var j = 0; j < values.length; j++) {
          if (v.options[j] !== values[j]) { match = false; break; }
        }
        if (match) return v;
      }
      return null;
    }

    function applyPlatinumRule() {
      if (!metalSelect || !colourSelect || metalSelect === colourSelect) return;
      var isPlatinum = /platinum/i.test(metalSelect.value);
      var wrap = colourSelect.closest("[data-option-wrap]");
      if (wrap) wrap.hidden = isPlatinum;

      /* When hidden, park the colour on whichever value actually has a platinum
         variant, so the id we submit is real rather than an impossible pairing. */
      if (isPlatinum) {
        var metalIdx = parseInt(metalSelect.getAttribute("data-option-index"), 10);
        var colourIdx = parseInt(colourSelect.getAttribute("data-option-index"), 10);
        for (var i = 0; i < variants.length; i++) {
          if (/platinum/i.test(variants[i].options[metalIdx] || "")) {
            colourSelect.value = variants[i].options[colourIdx];
            break;
          }
        }
      }
    }

    /* Mark values that cannot be reached with the other selections as they stand.
       Matters once a product has two options — Metal and Carat, say — where not
       every pairing is made. Without this a shopper picks a carat, gets a dead
       Add to cart, and has to guess which combination is the problem. */
    /* true while any option is still on its "Choose…" placeholder */
    function awaitingChoice() {
      return chosen().some(function (v) { return v === ""; });
    }

    function markUnavailable() {
      if (optionSelects.length < 2) return;
      /* with a blank still in play every trial combination fails, which would
         wrongly label the whole list "made to order" */
      if (awaitingChoice()) return;
      var current = chosen();
      optionSelects.forEach(function (sel, idx) {
        Array.prototype.forEach.call(sel.options, function (opt) {
          var trial = current.slice();
          trial[idx] = opt.value;
          var v = findVariant(trial);
          var reachable = !!v && v.available;
          opt.disabled = false;                 /* never trap the current choice */
          opt.textContent = reachable
            ? opt.value
            : opt.value + " — made to order";
        });
      });
    }

    function update() {
      applyPlatinumRule();
      markUnavailable();

      if (awaitingChoice()) {
        if (addBtn) {
          addBtn.disabled = true;
          addBtn.textContent = "Choose an option";
        }
        return;
      }

      var v = findVariant(chosen());

      if (v) {
        variantIdInput.value = v.id;
        if (priceEl) priceEl.textContent = money(v.price);
        if (addBtn) {
          /* Same three states as the Liquid above, so switching metal or carat
             keeps the button honest. A variant with no stock that Shopify still
             reports as available is one the studio will make — orderable, and
             labelled so the shopper knows it is not coming off a shelf. */
          var tracked = !!v.inventory_management;
          var noStock = tracked && v.inventory_quantity <= 0;

          addBtn.disabled = !v.available;
          if (!v.available) {
            addBtn.textContent = addBtn.dataset.labelUnavailable || "Made to order — contact us";
          } else if (noStock) {
            addBtn.textContent = addBtn.dataset.labelMadeToOrder || "Made to order";
          } else {
            addBtn.textContent = addBtn.dataset.labelInStock || "Add to cart";
          }
        }
      } else if (addBtn) {
        /* no such combination exists — say so rather than submitting a stale id */
        addBtn.disabled = true;
        addBtn.textContent = "Unavailable in this combination";
      }
    }

    optionSelects.forEach(function (s) { s.addEventListener("change", update); });
    update();
  }
})();
